import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { fmtDuration } from "@/lib/time";
import { LoadingState, EmptyState } from "@/components/states";
import { cn } from "@/lib/utils";

const PERIODS = [["week", "Week"], ["month", "Month"]];

export function MyProgressView() {
  const [period, setPeriod] = useState("week");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true; setLoading(true);
    apiFetch(`/time/my-progress?period=${period}`).then((r) => alive && setD(r))
      .catch((e) => toast.error(e.message)).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [period]);

  if (loading) return <LoadingState label="Loading your progress…" />;
  const hasData = d && (d.tasks_completed > 0 || d.time_tracked_seconds > 0);
  const byAct = d?.by_activity || {};
  const aw = d?.active_waiting || {};

  return (
    <div data-testid="my-progress-view">
      <div className="mb-4 flex items-center gap-1 rounded-lg border border-border p-0.5 w-fit">
        {PERIODS.map(([k, l]) => (
          <button key={k} onClick={() => setPeriod(k)} data-testid={`progress-period-${k}`}
            className={cn("rounded px-3 py-1 text-xs font-medium", period === k ? "bg-secondary text-foreground" : "text-muted-foreground")}>{l}</button>
        ))}
      </div>

      {!hasData ? (
        <EmptyState title="Not enough data yet" message="Complete tasks and track a little time — your progress will build up here. No numbers are invented." />
      ) : (
        <>
          <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Tasks completed" value={d.tasks_completed} sub={`${period === "week" ? "this week" : "this month"}`} />
            <Stat label="On-time" value={d.on_time_pct != null ? `${d.on_time_pct}%` : "—"} sub={d.with_due_count ? `${d.on_time_completed}/${d.with_due_count} with a due date` : "no due dates set"} />
            <Stat label="Time tracked" value={fmtDuration(d.time_tracked_seconds)} sub="active work" />
            <Stat label="Waiting time" value={fmtDuration(aw.waiting_seconds)} sub="currently blocked" />
          </div>

          {aw.active_seconds != null && (aw.active_seconds > 0 || aw.waiting_seconds > 0) && (
            <Card className="mb-4 p-4">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Active vs waiting</div>
              <p className="text-sm text-muted-foreground">Active tracked work is measured separately from time spent waiting/blocked — the full task age is never reported as active work.</p>
              <div className="mt-3 flex gap-4 text-sm">
                <span><span className="font-medium text-foreground">{fmtDuration(aw.active_seconds)}</span> active</span>
                <span><span className="font-medium text-warning">{fmtDuration(aw.waiting_seconds)}</span> waiting</span>
              </div>
            </Card>
          )}

          {Object.keys(byAct).length > 0 && (
            <Card className="mb-4 p-4">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Time by activity</div>
              <div className="space-y-1.5">
                {Object.entries(byAct).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-sm"><span>{k}</span><span className="text-muted-foreground">{fmtDuration(v)}</span></div>
                ))}
              </div>
            </Card>
          )}

          {d.estimate_vs_actual?.length > 0 && (
            <Card className="p-4">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Estimate vs actual</div>
              <p className="mb-3 text-xs text-muted-foreground">Shown only where an estimate exists — a neutral comparison for planning, not a score.</p>
              <div className="space-y-2">
                {d.estimate_vs_actual.map((e, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="min-w-0 flex-1 truncate">{e.task}</span>
                    <span className="text-muted-foreground">est {e.estimate_minutes}m · actual {e.actual_minutes}m</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <Card className="p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-heading text-2xl text-foreground">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </Card>
  );
}
